import React, { useEffect, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";
import { getPipelineMetrics } from "api/QuoteExpiration";
import { themevals } from "theme/themevals";

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color?: string;
}> = ({ label, value, sub, color }) => (
  <Box sx={{
    padding: "16px",
    borderRadius: "6px",
    border: "1px solid " + themevals.colors.inputOutline,
    backgroundColor: "#FFFFFF",
  }}>
    <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "24px", fontWeight: 700, color: color || themevals.colors.black, lineHeight: 1.2 }}>
      {value}
    </Box>
    <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "4px" }}>
      {label}
    </Box>
    {sub && (
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "11px", color: themevals.colors.dkgrey, marginTop: "2px", opacity: 0.7 }}>
        {sub}
      </Box>
    )}
  </Box>
);

const TrendIndicator: React.FC<{ current: number; previous: number; label: string }> = ({ current, previous, label }) => {
  const diff = current - previous;
  const isUp = diff > 0;
  const isDown = diff < 0;
  const arrow = isUp ? "\u2191" : isDown ? "\u2193" : "\u2013";
  const color = label === "Accepted" ? (isUp ? "#38A169" : isDown ? themevals.colors.danger : themevals.colors.dkgrey)
    : label === "Denied" || label === "Expired" ? (isDown ? "#38A169" : isUp ? themevals.colors.danger : themevals.colors.dkgrey)
    : themevals.colors.dkgrey;

  return (
    <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "6px" }}>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: themevals.colors.dkgrey, width: "70px" }}>{label}</Box>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", fontWeight: 600, color: themevals.colors.black, width: "30px", textAlign: "right" }}>{current}</Box>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 600, color: color }}>
        {arrow} {Math.abs(diff)} vs prev 30d
      </Box>
    </Flex>
  );
};

const PipelineMetricsWidget: React.FC = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const data = await getPipelineMetrics();
      if (data?.conversionRate) setMetrics(data);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{
        backgroundColor: "#FFFFFF", borderRadius: "6px", border: "1px solid", borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", marginBottom: "24px", padding: "24px", minHeight: "200px",
      }}>
        <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>Pipeline Health</Box>
        <Box sx={{ padding: "40px 0", textAlign: "center" }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "80px", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  if (!metrics) return null;

  const { conversionRate, timeToDecision, expirationEffectiveness, periodComparison, priceLockConversion } = metrics;

  return (
    <Box sx={{
      backgroundColor: "#FFFFFF", borderRadius: "6px", border: "1px solid", borderColor: themevals.colors.inputOutline,
      boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)", marginBottom: "24px", padding: "24px",
    }}>
      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: themevals.colors.black, marginBottom: "20px" }}>
        Pipeline Health
      </Box>

      {/* Key metrics cards */}
      <Grid columns={[2, 3, 5]} gap="12px" sx={{ marginBottom: "24px" }}>
        <MetricCard
          label="Conversion Rate"
          value={conversionRate.rate + "%"}
          sub={conversionRate.accepted + " of " + (conversionRate.accepted + conversionRate.denied + conversionRate.expired) + " decided"}
          color="#38A169"
        />
        <MetricCard
          label="Avg Days to Accept"
          value={String(timeToDecision.avgDaysToAccept)}
          sub={"Median: " + timeToDecision.medianDaysToAccept + " days"}
        />
        <MetricCard
          label="Avg Days to Deny"
          value={String(timeToDecision.avgDaysToDeny)}
        />
        <MetricCard
          label="Regeneration Rate"
          value={expirationEffectiveness.regenerationRate + "%"}
          sub={expirationEffectiveness.regenerated + " of " + expirationEffectiveness.totalExpired + " expired"}
          color={themevals.colors.info}
        />
        <MetricCard
          label="Open Quotes"
          value={String(conversionRate.open)}
          sub={"of " + conversionRate.total + " total (365d)"}
        />
        {priceLockConversion && priceLockConversion.totalLocked > 0 && (
          <MetricCard
            label="Lock Conversion"
            value={priceLockConversion.rate + "%"}
            sub={priceLockConversion.converted + " of " + priceLockConversion.totalLocked + " locked"}
            color="#2B6CB0"
          />
        )}
      </Grid>

      {/* 30-day trend comparison */}
      {(periodComparison.current30.accepted > 0 || periodComparison.current30.denied > 0 || periodComparison.current30.expired > 0 ||
        periodComparison.previous30.accepted > 0 || periodComparison.previous30.denied > 0 || periodComparison.previous30.expired > 0) && (
        <Box>
          <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, marginBottom: "12px" }}>
            30-Day Trend
          </Box>
          <TrendIndicator label="Accepted" current={periodComparison.current30.accepted} previous={periodComparison.previous30.accepted} />
          <TrendIndicator label="Denied" current={periodComparison.current30.denied} previous={periodComparison.previous30.denied} />
          <TrendIndicator label="Expired" current={periodComparison.current30.expired} previous={periodComparison.previous30.expired} />

          {/*
            WHAT THE TREND CANNOT SEE, SAID OUT LOUD.

            These counts are dated by acceptedDate / deniedDate /
            expiredAt. 511 accepted quotes carry no acceptedDate at all,
            so they sit in no window and no comparison.

            The previous version hid this by falling back to updatedAt,
            which does not record when anything happened - two bulk
            update days (the 2026-06-11 expiration backfill and a
            2026-07-20 run that touched 630 accepted quotes) dumped
            hundreds of old rows into whichever window contained them.
            That read as 498 accepted in a month against 693 in the
            app's entire history.

            Omitting them is right. Omitting them SILENTLY is not - a
            reader cannot tell a quiet month from a month the data
            cannot describe. The fix is populating the write paths
            (KNOWN_ISSUES item 27), not guessing here.
          */}
          {periodComparison.undated &&
            (periodComparison.undated.accepted > 0 ||
              periodComparison.undated.denied > 0 ||
              periodComparison.undated.expired > 0) && (
              <Box
                sx={{
                  marginTop: "12px",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  backgroundColor: "#FFF8E1",
                  border: "1px solid #E7CC7A",
                  fontFamily: "Poppins",
                  fontSize: "11px",
                  color: themevals.colors.dkgrey,
                  lineHeight: 1.5,
                }}
              >
                Not counted above:{" "}
                {[
                  periodComparison.undated.accepted > 0
                    ? periodComparison.undated.accepted + " accepted"
                    : null,
                  periodComparison.undated.denied > 0
                    ? periodComparison.undated.denied + " denied"
                    : null,
                  periodComparison.undated.expired > 0
                    ? periodComparison.undated.expired + " expired"
                    : null,
                ]
                  .filter(Boolean)
                  .join(", ")}{" "}
                with no recorded date. They cannot be placed in a period.
              </Box>
            )}
        </Box>
      )}
    </Box>
  );
};

export default PipelineMetricsWidget;
